// Reading a listing's stock number out loud.
//
// A seller picks the unit they trade in — "head", "tray (30 eggs)", "bag (90
// KG)", "day (hire)" — and it is stored as a free-form string (the API keeps
// `unit` as an unvalidated String). The stock number is a count of *that* unit,
// so labelling it "KG" is wrong for everything except the sellers who happen to
// list in kilos.
//
// The mobile app carries the same rules in `constants/units.js` (separate repo,
// so this is a deliberate copy rather than a shared import). Keep the two
// saying the same thing — a seller reading "100 trays" in the app and an admin
// reading "100 KG" for the same listing is how a restock goes out wrong.

// LEGACY, mirroring the app's `constants/units.js`. Hiring is no longer offered
// on the platform and the hire units were removed from the seller's picker, so
// this only covers listings published before that. Delete it, and the branch
// below, once none are stored with a hire unit.
//
// A hire unit prices a rental period, not a thing the seller holds a pile of.
// "K500 /day (hire)" is priced per day, but the number beside it is how many
// machines are free to hire, never how many days.
export function isHireUnit(unit) {
    return /\(\s*hire\s*\)\s*$/i.test(String(unit || ''));
}

// Units that read the same counted as they do alone — "50 head of cattle",
// "20 kg", "3 dozen". Everything else takes a plural.
const INVARIANT = new Set(['kg', 'head', 'dozen']);

// Plural label for counting: "bag (90 KG)" -> "bags", "bunch" -> "bunches",
// "head" -> "head". The size in brackets is dropped — it is already spelled out
// on the price, and "Quantity (bag (90 KG))" reads badly.
export function unitLabel(unit) {
    if (isHireUnit(unit)) return 'units'; // what's counted is machines, not days
    const base = String(unit || 'unit').replace(/\s*\(.*\)\s*$/, '').trim();
    if (!base) return 'units';
    if (INVARIANT.has(base)) return base;
    // box -> boxes, bunch -> bunches
    return /(s|x|z|ch|sh)$/i.test(base) ? `${base}es` : `${base}s`;
}

// What follows a stock number on screen: "100 trays", "1,000 kg", and for a
// hire listing "3 available to hire".
export function stockUnit(unit) {
    return isHireUnit(unit) ? 'available to hire' : unitLabel(unit);
}

// The units a seller can pick, per category — a copy of UNITS_BY_CATEGORY in
// the app's `constants/units.js`. Only used by the admin edit form, so an admin
// can't put a listing in a unit the app doesn't offer. Keep the two in step.
export const UNITS_BY_CATEGORY = {
    produce: [
        'bag (90 KG)',
        'bag (50 KG)',
        'bag (25 KG)',
        'bag (10 KG)',
        'bucket (20 L)',
        'tin (5 L)',
        'heap',
        'kg',
        'tonne',
        'crate',
        'box',
        'bunch',
        'tray',
        'litre',
        'piece',
    ],
    livestock: ['head', 'kg', 'bird', 'tray (30 eggs)', 'dozen', 'litre'],
    equipment: ['unit', 'set', 'piece'],
};

export function unitsFor(category) {
    return UNITS_BY_CATEGORY[category] || UNITS_BY_CATEGORY.produce;
}
